import { css } from "@emotion/css";
import { Badge, Callout, Checkbox } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { Permission, PriorityMode } from "@octopusdeploy/octopus-server-client";
import { useIsTrial } from "~/areas/configuration/components/LicenseUsage/useIsTrial";
import { PriorityDeploymentContextualHelp } from "~/components/ContextualHelp/ContextualHelpSnippets";
import { useIsLicensedFeatureEnabled } from "~/components/LicensedFeature/useIsLicensedFeatureEnabled";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { RequestTrial } from "~/components/RequestTrial/RequestTrial";
import ExpandableFormSection from "../../../../../components/form/Sections/ExpandableFormSection";
import Summary from "../../../../../components/form/Sections/Summary";
interface PriorityOptionsProps {
    priority: PriorityMode;
    context: "deployment" | "runbook";
    onChange(value: boolean): void;
}
const PriorityOptions = (props: PriorityOptionsProps) => {
    const hasPermissionToChangePriority = isAllowed({ permission: [Permission.TaskPrioritize] });
    const isLicensed = useIsLicensedFeatureEnabled("PriorityTask");
    const canChange = hasPermissionToChangePriority && isLicensed;
    const titleChip = useIsTrial() ? <Badge variant={"upsell"} label={"Enterprise"} size={"medium"} hideIcon={true}/> : undefined;
    const getHelp = () => (props.context === "deployment" ? "Choose whether to prioritize this deployment" : "Choose whether to prioritize this runbook");
    const checkboxLabel = props.context === "deployment" ? "Prioritize this deployment" : "Prioritize this runbook";
    const buildSummary = (priorityMode: PriorityMode) => (priorityMode === PriorityMode.On ? Summary.summary(`${props.context === "deployment" ? "Deploy" : "Run"} with priority`) : Summary.default("No priority"));
    return (<ExpandableFormSection title="Priority" chip={titleChip} errorKey="priority" help={getHelp()} summary={buildSummary(props.priority)}>
            {!hasPermissionToChangePriority && (<Callout title="Missing permission" type={"information"}>
                    You require the TaskPrioritize permission to change the priority of this {props.context === "deployment" ? "deployment" : "runbook run"}.
                </Callout>)}
            {hasPermissionToChangePriority && !isLicensed && (<RequestTrial type="inline-message" featureName={"Priority Deployments"} onRequestTrial={async (repository) => await repository.Deployments.requestPriorityDeploymentsTrial()} content="Priority is available on the Enterprise tier." dialogTitle="Request a free trial of Priority"/>)}
            <div className={styles.checkboxContainer}>
                <Checkbox disabled={!canChange} label={checkboxLabel} value={props.priority === PriorityMode.On} onChange={props.onChange} noMargin={true}/> <PriorityDeploymentContextualHelp context={props.context}/>
            </div>
        </ExpandableFormSection>);
};
const styles = {
    checkboxContainer: css({
        // align checkbox and contextual help on one line
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: space[8],
    }),
};
export default PriorityOptions;
